/**
 * Maps IANA timezone identifiers to BCP 47 locale tags for date/number formatting.
 * Used to derive locale-appropriate date formats from the user's selected timezone.
 */
const TIMEZONE_LOCALE_MAP: Record<string, string> = {
  // Europe
  'Europe/London':     'en-GB',
  'Europe/Dublin':     'en-IE',
  'Europe/Lisbon':     'pt-PT',
  'Europe/Paris':      'fr-FR',
  'Europe/Brussels':   'fr-BE',
  'Europe/Amsterdam':  'nl-NL',
  'Europe/Berlin':     'de-DE',
  'Europe/Zurich':     'de-CH',
  'Europe/Vienna':     'de-AT',
  'Europe/Rome':       'it-IT',
  'Europe/Madrid':     'es-ES',
  'Europe/Warsaw':     'pl-PL',
  'Europe/Prague':     'cs-CZ',
  'Europe/Stockholm':  'sv-SE',
  'Europe/Copenhagen': 'da-DK',
  'Europe/Helsinki':   'fi-FI',
  'Europe/Athens':     'el-GR',
  'Europe/Bucharest':  'ro-RO',
  'Europe/Kiev':       'uk-UA',
  'Europe/Moscow':     'ru-RU',
  'Europe/Istanbul':   'tr-TR',

  // North America
  'America/New_York':    'en-US',
  'America/Toronto':     'en-CA',
  'America/Chicago':     'en-US',
  'America/Denver':      'en-US',
  'America/Phoenix':     'en-US',
  'America/Los_Angeles': 'en-US',
  'America/Vancouver':   'en-CA',
  'America/Anchorage':   'en-US',
  'Pacific/Honolulu':    'en-US',

  // Central America
  'America/Mexico_City': 'es-MX',
  'America/Cancun':      'es-MX',
  'America/Panama':      'es-PA',

  // South America
  'America/Bogota':      'es-CO',
  'America/Lima':        'es-PE',
  'America/Santiago':    'es-CL',
  'America/Buenos_Aires':'es-AR',
  'America/Sao_Paulo':   'pt-BR',
  'America/Bahia':       'pt-BR',
  'America/Fortaleza':   'pt-BR',
  'America/Recife':      'pt-BR',
  'America/Belem':       'pt-BR',
  'America/Manaus':      'pt-BR',
  'America/Cuiaba':      'pt-BR',
  'America/Porto_Velho': 'pt-BR',
  'America/Rio_Branco':  'pt-BR',
  'America/Noronha':     'pt-BR',

  // Asia – Middle East
  'Asia/Dubai':      'ar-AE',
  'Asia/Riyadh':     'ar-SA',
  'Asia/Jerusalem':  'he-IL',
  'Asia/Tehran':     'fa-IR',

  // Asia – South Asia
  'Asia/Karachi':    'ur-PK',
  'Asia/Kolkata':    'en-IN',
  'Asia/Dhaka':      'bn-BD',

  // Asia – Southeast Asia
  'Asia/Bangkok':       'th-TH',
  'Asia/Ho_Chi_Minh':   'vi-VN',
  'Asia/Jakarta':       'id-ID',
  'Asia/Singapore':     'en-SG',
  'Asia/Kuala_Lumpur':  'ms-MY',
  'Asia/Manila':        'en-PH',

  // Asia – East Asia
  'Asia/Hong_Kong': 'zh-HK',
  'Asia/Shanghai':  'zh-CN',
  'Asia/Taipei':    'zh-TW',
  'Asia/Seoul':     'ko-KR',
  'Asia/Tokyo':     'ja-JP',

  // Pacific / Oceania
  'Australia/Perth':     'en-AU',
  'Australia/Adelaide':  'en-AU',
  'Australia/Brisbane':  'en-AU',
  'Australia/Sydney':    'en-AU',
  'Australia/Melbourne': 'en-AU',
  'Pacific/Auckland':    'en-NZ',

  // UTC
  'UTC':     'en-US',
  'Etc/UTC': 'en-US',
};

/**
 * Fallback locale derived from the IANA region prefix when the timezone
 * is not in the curated map (e.g. user's browser timezone).
 */
function localeFromPrefix(timezone: string): string {
  const prefix = timezone.split('/')[0];
  switch (prefix) {
    case 'America':    return 'en-US';
    case 'Europe':     return 'en-GB';
    case 'Asia':       return 'en-US';
    case 'Australia':  return 'en-AU';
    case 'Pacific':    return 'en-US';
    case 'Africa':     return 'en-US';
    case 'Atlantic':   return 'en-US';
    case 'Indian':     return 'en-US';
    case 'Arctic':     return 'en-US';
    case 'Antarctic':  return 'en-US';
    default:           return 'en-US';
  }
}

/**
 * Returns a BCP 47 locale tag appropriate for the given IANA timezone.
 * Used to format dates and times in a way that matches the timezone's region.
 *
 * @param timezone - IANA timezone identifier (e.g. 'Europe/Berlin', 'America/New_York')
 * @returns BCP 47 locale tag (e.g. 'de-DE', 'en-US')
 *
 * @example
 * getLocaleForTimezone('Europe/Berlin')    // 'de-DE'
 * getLocaleForTimezone('America/New_York') // 'en-US'
 * getLocaleForTimezone('Asia/Tokyo')       // 'ja-JP'
 */
export function getLocaleForTimezone(timezone: string): string {
  if (!timezone) return 'en-US';
  return TIMEZONE_LOCALE_MAP[timezone] ?? localeFromPrefix(timezone);
}
